import { Text } from '@cretesuite/design-system'
import {
  IonBadge,
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSpinner,
} from '@ionic/react'
import { msg, plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { checkmarkCircle } from 'ionicons/icons'
import { useEffect, useState } from 'react'
import { useQuery } from 'urql'
import { Logo } from '../components/Logo'
import { useSyncStatus } from '../context/SyncStatusProvider'
import { MyOrganizationsQuery } from '../graphql/organizations'
import { reloadTo } from '../helpers/appReload'
import { useOrgSwitch } from '../hooks/useOrgSwitch'

type Organization = {
  id: number
  name: string
  logo?: string | null
}

const OrgAvatar = ({ org }: { org: Organization }) => {
  if (org.logo) {
    return (
      <img slot='start' src={org.logo} alt='' className='org-selector-logo' />
    )
  }
  return (
    <div slot='start' className='org-selector-initial' aria-hidden='true'>
      {org.name.charAt(0).toUpperCase()}
    </div>
  )
}

/**
 * Post-login organization picker for multi-org operators. Only reached via the
 * login redirect; a direct hit with a single org falls through to /welcome.
 */
export const OrgSelectorPage = () => {
  const { _ } = useLingui()
  const [{ data, fetching, error }, refetchOrganizations] = useQuery({
    query: MyOrganizationsQuery,
  })
  const { activeTid, blockedReason, switchTo } = useOrgSwitch()
  const { pendingCount, isSyncing, forceSync } = useSyncStatus()
  const [switchingTid, setSwitchingTid] = useState<number | null>(null)

  const organizations = data?.myOrganizations
  const loadFailed = Boolean(error) && !organizations

  // Only a genuine single-org account lands on /welcome. A failed lookup must
  // not do the same — it would strand a multi-org operator in one tenant.
  useEffect(() => {
    if (fetching || loadFailed) return
    if (organizations && organizations.length <= 1) reloadTo('/welcome')
  }, [fetching, loadFailed, organizations])

  const handleSelect = async (tid: number) => {
    if (switchingTid !== null) return
    setSwitchingTid(tid)
    const switched = await switchTo(tid)
    if (!switched) setSwitchingTid(null)
  }

  const pendingSync = blockedReason === 'pending-sync'

  return (
    <IonPage>
      <IonContent fullscreen className='ion-padding'>
        <div className='welcome-layout'>
          <div className='welcome-logo'>
            <Logo />
          </div>

          <h1 className='welcome-heading'>{_(msg`Select Organization`)}</h1>

          <Text variant='body' className='welcome-message'>
            {loadFailed
              ? _(
                  msg`We couldn't load your organizations. Check your connection and try again.`,
                )
              : _(
                  msg`Your account belongs to more than one organization. Choose which one to work in — you can switch later from your Profile.`,
                )}
          </Text>

          {pendingSync && (
            <div className='org-selector-callout'>
              <Text variant='body'>
                <IonBadge color='warning'>{pendingCount}</IonBadge>{' '}
                {_(
                  plural(pendingCount, {
                    one: '# change is waiting to sync. Sync before switching organizations.',
                    other:
                      '# changes are waiting to sync. Sync before switching organizations.',
                  }),
                )}
              </Text>
              <IonButton size='small' onClick={forceSync} disabled={isSyncing}>
                {isSyncing ? <IonSpinner name='crescent' /> : _(msg`Sync now`)}
              </IonButton>
            </div>
          )}

          {fetching ? (
            <IonSpinner name='crescent' />
          ) : loadFailed ? (
            <IonButton
              data-testid='org-selector-retry'
              onClick={() =>
                refetchOrganizations({ requestPolicy: 'network-only' })
              }
            >
              {_(msg`Try again`)}
            </IonButton>
          ) : (
            <IonList lines='full' className='org-selector-list'>
              {organizations?.map((org) => {
                const isActive = org.id === activeTid
                const isBlocked = blockedReason !== null && !isActive
                return (
                  <IonItem
                    key={org.id}
                    button={!isBlocked}
                    disabled={isBlocked || switchingTid !== null}
                    detail={false}
                    onClick={() => handleSelect(org.id)}
                    data-testid={`org-item-${org.id}`}
                  >
                    <OrgAvatar org={org} />
                    <IonLabel>{org.name}</IonLabel>
                    {switchingTid === org.id ? (
                      <IonSpinner slot='end' name='crescent' />
                    ) : isActive ? (
                      <IonIcon
                        slot='end'
                        icon={checkmarkCircle}
                        color='primary'
                      />
                    ) : null}
                  </IonItem>
                )
              })}
            </IonList>
          )}
        </div>
      </IonContent>
    </IonPage>
  )
}

export default OrgSelectorPage
